import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import dayjs from "dayjs";
import { BrandCampaign } from "../../../shared/models/brandCampaign";

export interface BrandCampaignSubmitCheckDialogData {
  campaign: BrandCampaign;
}
export type BrandCampaignSubmitCheckDialogResult = boolean | undefined;

@Component({
  selector: "app-brand-campaign-submit-check-dialog",
  templateUrl: "./brand-campaign-submit-check-dialog.component.html",
  styleUrls: ["./brand-campaign-submit-check-dialog.component.scss"],
})
export class BrandCampaignSubmitCheckDialogComponent {
  protected readonly campaign: BrandCampaign;
  protected readonly campaignInformationCompleted: boolean;

  protected readonly campaignNameCheck: boolean = false;
  protected readonly inviteLinkCheck: boolean = false;
  protected readonly inviteCodeCheck: boolean = false;
  protected readonly timeFrameCheck: boolean = false;
  protected readonly featImgCheck: boolean = false;
  protected readonly inviteImgCheck: boolean = false;
  protected readonly brandLogoCheck: boolean = false;
  protected readonly adsActiveCheck: boolean = false;
  protected readonly googleAdsEnabled: boolean = false;
  protected readonly googleAdsTargetingCheck: boolean = false;
  protected readonly contentScheduled: boolean = false;
  protected readonly contentWithinPeriod: boolean = false;
  protected readonly maxAdsScheduledCheck: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { campaign: BrandCampaign },
    protected readonly dialogRef: MatDialogRef<BrandCampaignSubmitCheckDialogComponent>,
  ) {
    this.campaign = data.campaign;

    this.campaignNameCheck = !!this.campaign.name;
    this.inviteLinkCheck = !!this.campaign.projectName;
    this.inviteCodeCheck = !!this.campaign.invitationCode;
    this.timeFrameCheck = !!this.campaign.startDate && !!this.campaign.endDate;
    this.featImgCheck = this.campaign.currentDetails.sliders?.length > 0;
    this.inviteImgCheck = !!this.campaign.signUpBackgroundImage;
    this.brandLogoCheck =
      !!this.campaign.signUpLogo || !!this.campaign.defaultBrandLogoUrl;
    this.adsActiveCheck = this.campaign.promoAdsEnabled;

    this.googleAdsEnabled =
      this.campaign.promoAdsEnabled && this.campaign.useGoogleAds;
    this.googleAdsTargetingCheck = !(
      this.googleAdsEnabled && !(this.campaign.googleAdsAudienceIds?.length > 0)
    );

    this.contentScheduled =
      this.campaign.currentCalendar?.calendarPosts.length > 0;
    this.maxAdsScheduledCheck = !(
      this.campaign.promoAdsEnabled &&
      !(
        this.campaign.totalAdsInCalendar === this.campaign.maxAdsCampaignPartner
      )
    );

    const campaignStartDate = dayjs(this.campaign.startDate)
      .startOf("day")
      .toDate();
    const campaignEndDate = dayjs(this.campaign.endDate).endOf("day").toDate();

    const someContentOutsidePeriod =
      this.campaign.currentCalendar?.calendarPosts.some((post) => {
        if (post.isGoogleAd || (post.isFacebookPost && post.willBecomeAd)) {
          if (this.campaign.adDuration) {
            const fullLengthEndDate = new Date(
              new Date(post.suggestedPublishDate).getTime() +
                this.campaign.adDuration * 24 * 60 * 60 * 1000,
            );
            if (fullLengthEndDate > campaignEndDate) {
              return true;
            }
          }
        }

        return (
          post.suggestedPublishDate < campaignStartDate ||
          post.suggestedPublishDate > campaignEndDate
        );
      });

    this.contentWithinPeriod = !someContentOutsidePeriod;

    const areAdsCorrect =
      !this.adsActiveCheck ||
      (this.adsActiveCheck &&
        this.googleAdsTargetingCheck &&
        this.maxAdsScheduledCheck);

    this.campaignInformationCompleted =
      this.campaignNameCheck &&
      this.inviteLinkCheck &&
      this.inviteCodeCheck &&
      this.timeFrameCheck &&
      this.featImgCheck &&
      this.inviteImgCheck &&
      this.brandLogoCheck &&
      this.contentScheduled &&
      this.contentWithinPeriod &&
      areAdsCorrect;
  }
}
